import React, { useEffect, useState, useCallback } from "react";
import {
    Box,
    Grid,
    Button,
    Dialog,
    Autocomplete,
    TextField,
    Typography,
    Avatar,
    Tabs,
    Tab,
    Chip,
    Drawer,
    IconButton,
    InputAdornment,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import CancelIcon from "@mui/icons-material/Cancel";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import moment from "moment";
import MasterCommonTable from "../../sharedComponents/MaterCommonTable/MasterCommonTable";
import { ToastError, ToastSuccess } from "../../utils/Toaster/Toaster";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../../context/AuthProvider";
import AddTodoTask from "../../components/TodoTask/AddTodoTask";
import { deleteTodoTask, getTodoTask, getTodoTaskByProjectLeader } from "../../services/todoTask";
import TodoTaskHistory from "../../components/TodoTask/TodoTaskHistory";
import { getProject } from "../../services/project.service";
import { getUser, getTokenbyData } from "../../services/user.service";
import PermissionGate from "../../components/Permission/PermissionGate";
import { usePermissions } from "../../context/PermissionProvider";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import TodoTaskBoard from "./components/TodoTaskBoard";
import { TODO_TASK_STATUS } from "../../common/constant/constant";

const { RangePicker } = DatePicker;



const THEME_COLOR = "#008080";

const TodoTask = () => {
    const [showFilters, setShowFilters] = useState(false);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [openHistoryDrawer, setOpenHistoryDrawer] = useState(false);
    const [data, setData] = useState([]);
    const [valueData, setValueData] = useState([]);
    const { accessToken } = useAuth();
    const { canAdd, canEdit, canDelete } = usePermissions();
    const [selectedData, setSelectedData] = useState({});
    const [tableTotalCount, setTableCount] = useState(0);
    const [user, setUser] = useState({});
    const [isAdmin, setIsAdmin] = useState(false);
    const [projects, setProjects] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [currentTab, setCurrentTab] = useState(0); // 0 = Created, 1 = Assigned
    const [dateRange, setDateRange] = useState([
        dayjs().startOf('month'),
        dayjs().endOf('month')
    ]);
    const [queryParam, setQueryParam] = useState({
        status: null,
        priority: null,
        projectId: null,
        userId: null,
        createdByIds: null,
        assignedToIds: null,
        date: null,
        startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
        endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
        tab: "created",
        page: 1,
        limit: 10,
        sort: "-createdAt",
        isAdmin: false,
    });

    const getTodoTaskData = async () => {
        try {
            const queryParams = {
                ...queryParam,
                userId: user.id,
            };

            console.log("API Query Params:", queryParams);
            let res;
            if (queryParams.tab === "myProjects") {
                res = await getTodoTaskByProjectLeader(queryParams);
            } else {
                res = await getTodoTask(queryParams);
            }

            const getStatusLabel = (status) => {
                switch (status) {
                    case TODO_TASK_STATUS.PENDING:
                        return "Pending";
                    case TODO_TASK_STATUS.COMPLETED:
                        return "Completed";
                    case TODO_TASK_STATUS.CANCELED:
                        return "Canceled";
                    case TODO_TASK_STATUS.ACKNOWLEDGE:
                        return "Acknowledge";
                    default:
                        return "-";
                }
            };

            const modifiedData = res.data.data.map((e) => {
                const priorityLabels = { 0: "Low", 1: "Medium", 2: "High" };
                const priorityLabel = priorityLabels[e.priority] || "Low";

                return {
                    ...e,
                    statusLabel: getStatusLabel(e.status),
                    statusLabel: getStatusLabel(e.status),
                    priorityLabel: priorityLabel, // "High"
                    priorityLevel: e.priorityLevel || "-", // Added Level mapping
                    projectName: e?.project_info?.name || "-",
                    createdByName: e?.createdBy_info
                        ? `${e.createdBy_info.firstName} ${e.createdBy_info.lastName}`
                        : "-",
                    assignedToName: e?.assignedTo_info
                        ? `${e.assignedTo_info.firstName} ${e.assignedTo_info.lastName}`
                        : "-",
                    completionDateFormatted: e?.completionDate
                        ? moment(e.completionDate).format("DD/MM/YYYY")
                        : "-",
                };
            })
            setData(modifiedData);
            setValueData(res.data);
            setTableCount(res?.data?.totalCount || 0);
            console.log("TodoTask Data Sample:", modifiedData[0]); // Debug log
        } catch (error) {
            console.log(error);
        }
    };

    const fetchProjects = async () => {
        try {
            if (queryParam.tab === "myProjects") {
                const response = await getProject({ isEnabled: true, ProjectLeadId: user.id });
                setProjects(response.data.data);
            } else {
                const response = await getProject({ isEnabled: true });
                setProjects(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching projects:", error);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await getUser({ isEnabled: true, isAdmin: true });
            setUsers(response.data.data);
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };

    useEffect(() => {
        fetchProjects();
    }, [user, queryParam]);

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        const fetchAdminStatus = async () => {
            try {
                const res = await getTokenbyData();
                const usersData = res.data;
                const adminStatus = usersData?.isOverAllAdmin || false;
                setIsAdmin(adminStatus);
                setQueryParam(prev => ({
                    ...prev,
                    isAdmin: adminStatus,
                    tab: adminStatus ? "all" : "created"
                }));
            } catch (error) {
                console.error("Error fetching admin status:", error);
            }
        };
        fetchAdminStatus();
    }, []);

    useEffect(() => {
        if (accessToken) {
            try {
                const decodedToken = jwtDecode(accessToken);
                setUser(decodedToken);
                console.log("Decoded Token / User:", decodedToken); // Debug log
            } catch (error) {
                console.error("Error decoding token:", error);
            }
        }
    }, [accessToken]);

    useEffect(() => {
        if (user?.id) {
            getTodoTaskData();
        }
    }, [user, queryParam]);

    const handleAdd = () => {
        setOpenDrawer(true);
        setSelectedData({});
    };

    const statusOptions = [
        { label: "All", value: null },
        { label: "Pending", value: TODO_TASK_STATUS.PENDING },
        { label: "Completed", value: TODO_TASK_STATUS.COMPLETED },
        { label: "Canceled", value: TODO_TASK_STATUS.CANCELED },
        { label: "Acknowledge", value: TODO_TASK_STATUS.ACKNOWLEDGE },
    ];

    const priorityOptions = [
        { label: "All", value: null },
        { label: "Low", value: 0 },
        { label: "Medium", value: 1 },
        { label: "High", value: 2 },
    ];

    const handleView = (e) => {
        e.isView = true;
        setOpenDrawer(true);
        setSelectedData(e);
    };

    const handleEdit = (e) => {
        e.isView = false;
        setOpenDrawer(true);
        setSelectedData(e);
    };

    const handleDelete = async (e) => {
        try {
            const res = await deleteTodoTask(e._id);
            getTodoTaskData();
            ToastSuccess(res.data.message);
        } catch (error) {
            console.log(error);
            ToastError(error?.data?.message || "Something went wrong");
        }
    };

    const handleHistory = (e) => {
        setOpenHistoryDrawer(true);
        setSelectedData(e);
    };

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
        setSelectedProject(null); // Reset selected project
        let newTabValue;
        if (isAdmin) {
            if (newValue === 0) newTabValue = "all";
            else if (newValue === 1) newTabValue = "created";
            else if (newValue === 2) newTabValue = "assigned";
            else newTabValue = "myProjects";
        } else {
            if (newValue === 0) newTabValue = "created";
            else if (newValue === 1) newTabValue = "assigned";
            else newTabValue = "myProjects";
        }

        // Reset date range to current month
        setDateRange([
            dayjs().startOf('month'),
            dayjs().endOf('month')
        ]);

        // Reset all filters when changing tabs
        setQueryParam({
            status: null,
            priority: null,
            projectId: null,
            userId: null,
            createdByIds: null,
            assignedToIds: null,
            date: null,
            startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
            endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
            tab: newTabValue,
            page: 1,
            limit: 10,
            sort: "-createdAt",
            isAdmin: isAdmin,
        });

    };

    const tableOutputEvent = useCallback((params) => {
        console.log("Table Output Event Params:", params);
        const { page, limit, sort } = params;

        setQueryParam(prev => {
            if (prev.page === page && prev.limit === limit && prev.sort === sort) {
                return prev;
            }
            console.log("Setting queryParam:", {
                page: page,
                limit: limit,
                sort: sort,
            });
            return {
                ...prev,
                page: page,
                limit: limit,
                sort: sort,
            };
        });
    }, []);

    const getColumns = () => {
        const baseColumns = [
            { id: "title", label: "Title" },
            { id: "description", label: "Description" },
            { id: "projectName", label: "Project" },
        ];

        if (queryParam.tab === "all" || queryParam.tab === "myProjects") {
            baseColumns.push({ id: "createdByName", label: "Created By" });
            baseColumns.push({ id: "assignedToName", label: "Assigned To" });
        } else if (queryParam.tab === "created") {
            baseColumns.push({ id: "assignedToName", label: "Assigned To" });
        } else if (queryParam.tab === "assigned") {
            baseColumns.push({ id: "createdByName", label: "Created By" });
        }

        baseColumns.push({ id: "priorityLabel", label: "Priority" });
        baseColumns.push({ id: "priorityLevel", label: "Level" }); // Added Level column
        baseColumns.push({ id: "completionDateFormatted", label: "Completion Date" });
        baseColumns.push({
            id: "statusLabel",
            label: "Status",
            render: (row) => {
                const getColor = () => {
                    switch (row.status) {
                        case TODO_TASK_STATUS.COMPLETED:
                            return "success";
                        case TODO_TASK_STATUS.PENDING:
                            return "warning";
                        case TODO_TASK_STATUS.CANCELED:
                            return "error";
                        case TODO_TASK_STATUS.ACKNOWLEDGE:
                            return "info";
                        default:
                            return "default";
                    }
                };
                return <Chip label={row.statusLabel} color={getColor()} size="small" />;
            }
        });

        return baseColumns;
    };

    const columns = getColumns();

    const tableConfig = {
        isMore: true,
        isAction: true,
        isAdmin: true,
        isView: true,
        isEdit: canEdit("todoTask"),
        isDelete: isAdmin,
        isHistory: true,
    };

    const commonAutocompleteSx = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: "white",
            '&.Mui-focused fieldset': {
                borderColor: THEME_COLOR,
            },
        },
    };

    const commonListboxProps = {
        sx: {
            '& .MuiAutocomplete-option': {
                fontFamily: '"Manrope", sans-serif',
                fontSize: '14px',
                color: '#333',
                '&[aria-selected="true"]': {
                    bgcolor: `${THEME_COLOR} !important`,
                    color: 'white !important',
                    '&.Mui-focused': {
                        bgcolor: `${THEME_COLOR} !important`,
                    },
                },
                '&.Mui-focused': {
                    bgcolor: `${THEME_COLOR}15`,
                },
            },
        }
    };

    return (
        <div className="mainPageRenderingContainer" style={{ padding: "0px" }}>
            <Box sx={{ height: "fit-content" }}>
                {/* Tabs - Only show for non-admin users */}
                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                    <Tabs
                        value={currentTab}
                        onChange={handleTabChange}
                        sx={{
                            "& .MuiTab-root": {
                                textTransform: "none",
                                fontSize: "14px",
                                fontWeight: 500,
                                color: "#666",
                            },
                            "& .Mui-selected": {
                                color: `${THEME_COLOR} !important`,
                            },
                            "& .MuiTabs-indicator": {
                                backgroundColor: THEME_COLOR,
                            },
                        }}
                    >
                        {isAdmin && <Tab label="All" />}
                        <Tab label="Created by Me" />
                        <Tab label="Assigned to Me" />
                        {!isAdmin && <Tab label="My Projects" />}
                    </Tabs>
                </Box>

                <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                    {/* Stats Cards Section */}
                    <Grid item xs={12} md={7}>
                        {/* Stats Cards Content - keeping original structure */}
                        <Grid container spacing={2} alignItems="center">
                            {/* Pending */}
                            <Grid item xs={6} sm={3}>
                                <Box
                                    display="flex"
                                    height="37px"
                                    fontFamily="inter"
                                    alignItems="center"
                                    bgcolor="#ffffff"
                                    borderRadius="12px"
                                    padding="8px 16px"
                                    gap={1}
                                    width={"100%"}
                                >
                                    <Avatar sx={{ bgcolor: "#fff4e5", width: 20, height: 20 }}>
                                        <PendingIcon sx={{ color: "#ff9800", fontSize: 12 }} />
                                    </Avatar>
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            color: "#555",
                                            fontWeight: "500",
                                            flex: 1,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        }}
                                    >
                                        Pending
                                        <Box component="span" fontWeight="bold" color="black">
                                            {valueData?.pendingCount || 0}
                                        </Box>
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Completed */}
                            <Grid item xs={6} sm={3}>
                                <Box
                                    display="flex"
                                    height="37px"
                                    alignItems="center"
                                    bgcolor="#ffffff"
                                    fontFamily="inter"
                                    borderRadius="12px"
                                    padding="8px 16px"
                                    gap={1}
                                >
                                    <Avatar sx={{ bgcolor: "#e8f5e9", width: 20, height: 20 }}>
                                        <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 12 }} />
                                    </Avatar>
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            color: "#555",
                                            fontWeight: "500",
                                            flex: 1,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        }}
                                    >
                                        Completed
                                        <Box component="span" fontWeight="bold" color="black">
                                            {valueData?.completedCount || 0}
                                        </Box>
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Canceled */}
                            <Grid item xs={6} sm={3}>
                                <Box
                                    display="flex"
                                    height="37px"
                                    alignItems="center"
                                    fontFamily="inter"
                                    bgcolor="#ffffff"
                                    borderRadius="12px"
                                    padding="8px 16px"
                                    gap={1}
                                >
                                    <Avatar sx={{ bgcolor: "#ffebee", width: 20, height: 20 }}>
                                        <CancelIcon sx={{ color: "#f44336", fontSize: 12 }} />
                                    </Avatar>
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            color: "#555",
                                            fontWeight: "500",
                                            flex: 1,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        }}
                                    >
                                        Canceled
                                        <Box component="span" fontWeight="bold" color="black">
                                            {valueData?.canceledCount || 0}
                                        </Box>
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Acknowledge */}
                            <Grid item xs={6} sm={3}>
                                <Box
                                    display="flex"
                                    height="37px"
                                    alignItems="center"
                                    fontFamily="inter"
                                    bgcolor="#ffffff"
                                    borderRadius="12px"
                                    padding="8px 16px"
                                    gap={1}
                                >
                                    <Avatar sx={{ bgcolor: "#e3f2fd", width: 20, height: 20 }}>
                                        <ThumbUpAltIcon sx={{ color: "#2196f3", fontSize: 12 }} />
                                    </Avatar>
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            color: "#555",
                                            fontWeight: "500",
                                            flex: 1,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        }}
                                    >
                                        Acknowledge
                                        <Box component="span" fontWeight="bold" color="black">
                                            {valueData?.acknowledgeCount || 0}
                                        </Box>
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Desktop Add Button (Moved here for alignment) */}
                    <Grid item xs={12} md={5} sx={{ display: { xs: "none", md: "flex" }, justifyContent: "flex-end" }}>
                        <PermissionGate module="todoTask" action="add">
                            <Button
                                startIcon={<AddOutlinedIcon />}
                                onClick={() => handleAdd()}
                                variant="contained"
                                size="medium"
                                sx={{
                                    height: "40px",
                                    textTransform: "capitalize",
                                    borderRadius: 2,
                                    boxShadow: 2,
                                    px: 3,
                                    bgcolor: THEME_COLOR,
                                    "&:hover": { bgcolor: "#006666" },
                                }}
                            >
                                Add TodoTask
                            </Button>
                        </PermissionGate>
                    </Grid>

                </Grid>

                {/* Mobile Top Buttons (Conditionally rendered below tabs) */}
                <Box sx={{ display: { xs: "flex", md: "none", justifyContent: "space-between" }, gap: 1.5, mt: 1.5, mb: 2 }}>
                    <Button
                        variant={showFilters ? "contained" : "outlined"}
                        onClick={() => setShowFilters(!showFilters)}
                        sx={{
                            minWidth: "48px",
                            width: "48px",
                            height: "48px",
                            padding: 0,
                            borderColor: "#E5E7EB",
                            borderRadius: "12px",
                            color: showFilters ? "#fff" : "#6B7280",
                            backgroundColor: showFilters ? "#008080" : "#FFFFFF",
                            "&:hover": {
                                borderColor: "#D1D5DB",
                                backgroundColor: showFilters ? "#006666" : "#F9FAFB",
                            }
                        }}
                    >
                        {showFilters ? <CloseIcon /> : <FilterListIcon />}
                    </Button>

                    <PermissionGate module="todoTask" action="add">
                        <Button
                            startIcon={<AddOutlinedIcon />}
                            onClick={() => handleAdd()}
                            className="Add-Button"
                            variant="contained"
                            sx={{
                                flex: 1,
                                borderRadius: "12px",
                                py: 1.2,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "15px",
                                bgcolor: THEME_COLOR,
                                "&:hover": {
                                    bgcolor: "#006666"
                                }
                            }}
                        >
                            Add TodoTask
                        </Button>
                    </PermissionGate>
                </Box>



                {/* Filters Row (Desktop Only) */}
                <Grid container spacing={2} sx={{ mt: 1, display: { xs: "none", md: "flex" } }} alignItems="center">
                    {/* Project Filter */}
                    <Grid item xs={12} sm={2}>
                        <Autocomplete
                            options={projects}
                            getOptionLabel={(option) => option.name}
                            value={selectedProject}
                            isOptionEqualToValue={(option, value) => option._id === value._id}
                            onChange={(e, v) => {
                                setSelectedProject(v);
                                setQueryParam({
                                    ...queryParam,
                                    projectId: v ? v._id : null,
                                });
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Filter by Project"
                                    size="small"
                                    sx={{ bgcolor: "white" }}
                                />
                            )}
                        />
                    </Grid>


                    {/* Created By Filter - Hide if on "Created by Me" tab */}
                    {queryParam.tab !== "created" && (
                        <Grid item xs={12} sm={2.5}>
                            <Autocomplete
                                multiple
                                options={users}
                                getOptionLabel={(option) => option.name}
                                value={users.filter(u => (queryParam.createdByIds || "").split(",").includes(u._id)) || []}
                                isOptionEqualToValue={(option, value) => option._id === value._id}
                                onChange={(e, v) => {
                                    setQueryParam({
                                        ...queryParam,
                                        createdByIds: v.length ? v.map(u => u._id).join(",") : null,
                                    });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Filter by Creator"
                                        size="small"
                                        sx={{ bgcolor: "white" }}
                                    />
                                )}
                            />
                        </Grid>
                    )}

                    {/* Assigned To Filter - Hide if on "Assigned to Me" tab */}
                    {queryParam.tab !== "assigned" && (
                        <Grid item xs={12} sm={2.5}>
                            <Autocomplete
                                multiple
                                options={users}
                                getOptionLabel={(option) => option.name}
                                value={users.filter(u => (queryParam.assignedToIds || "").split(",").includes(u._id)) || []}
                                isOptionEqualToValue={(option, value) => option._id === value._id}
                                onChange={(e, v) => {
                                    setQueryParam({
                                        ...queryParam,
                                        assignedToIds: v.length ? v.map(u => u._id).join(",") : null,
                                    });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Filter by Assignee"
                                        size="small"
                                        sx={{ bgcolor: "white" }}
                                    />
                                )}
                            />
                        </Grid>
                    )}

                    {/* Date Range Filter */}
                    <Grid item xs={12} sm={2.5}>
                        <RangePicker
                            value={dateRange}
                            onChange={(date) => {
                                if (date && Array.isArray(date)) {
                                    setDateRange(date);
                                    setQueryParam({
                                        ...queryParam,
                                        date,
                                        startDate: date[0] ? dayjs(date[0]).format("YYYY-MM-DD") : null,
                                        endDate: date[1] ? dayjs(date[1]).format("YYYY-MM-DD") : null,
                                    });
                                } else {
                                    setDateRange(null);
                                    setQueryParam({
                                        ...queryParam,
                                        date: null,
                                        startDate: null,
                                        endDate: null,
                                    });
                                }
                            }}
                            className="w-100"
                            style={{ height: "40px" }}
                        />
                    </Grid>



                    {/* Priority Filter - Only show if NOT on All/Board tab */}
                    {queryParam.tab !== "all" && (
                        <Grid item xs={12} sm={2}>
                            <Autocomplete
                                options={priorityOptions}
                                disableClearable={true}
                                value={
                                    priorityOptions.find(
                                        (option) => option.value === queryParam.priority
                                    ) || priorityOptions[0]
                                }
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                onChange={(e, v) => {
                                    setQueryParam({
                                        ...queryParam,
                                        priority: v ? v.value : null,
                                    });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Filter by Priority"
                                        size="small"
                                        sx={{ bgcolor: "white" }}
                                    />
                                )}
                            />
                        </Grid>
                    )}

                    {/* Status Filter - Only show if NOT on All/Board tab */}
                    {queryParam.tab !== "all" && (
                        <Grid item xs={12} sm={2}>
                            <Autocomplete
                                options={statusOptions}
                                disableClearable={true}
                                value={
                                    statusOptions.find(
                                        (option) => option.value === queryParam.status
                                    ) || statusOptions[0]
                                }
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                onChange={(e, v) => {
                                    setQueryParam({
                                        ...queryParam,
                                        status: v ? v.value : null,
                                    });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Filter by Status"
                                        size="small"
                                        sx={{ bgcolor: "white" }}
                                    />
                                )}
                            />
                        </Grid>
                    )}

                </Grid>

                {/* Mobile Filters Drawer (Action Sheet Style) */}
                <Drawer
                    anchor="bottom"
                    open={showFilters}
                    onClose={() => setShowFilters(false)}
                    PaperProps={{
                        sx: {
                            borderTopLeftRadius: "20px",
                            borderTopRightRadius: "20px",
                            padding: "20px",
                            maxHeight: "85vh"
                        }
                    }}
                >
                    <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#111827" }}>
                            Filter Tasks
                        </Typography>
                        <IconButton onClick={() => setShowFilters(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Grid container spacing={2}>
                        {/* Project Filter */}
                        <Grid item xs={12}>
                            <Autocomplete
                                options={projects}
                                getOptionLabel={(option) => option.name}
                                value={selectedProject}
                                isOptionEqualToValue={(option, value) => option._id === value._id}
                                onChange={(e, v) => {
                                    setSelectedProject(v);
                                    setQueryParam({
                                        ...queryParam,
                                        projectId: v ? v._id : null,
                                    });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Filter by Project"
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "8px",
                                                bgcolor: "#FFFFFF",
                                                "& fieldset": { borderColor: "#E5E7EB" },
                                                "&.Mui-focused fieldset": { borderColor: THEME_COLOR }
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Created By Filter */}
                        {queryParam.tab !== "created" && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    multiple
                                    options={users}
                                    getOptionLabel={(option) => option.name}
                                    value={users.filter(u => (queryParam.createdByIds || "").split(",").includes(u._id)) || []}
                                    onChange={(e, v) => {
                                        setQueryParam({
                                            ...queryParam,
                                            createdByIds: v.length ? v.map(u => u._id).join(",") : null,
                                        });
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Filter by Creator"
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "8px",
                                                    bgcolor: "#FFFFFF",
                                                    "& fieldset": { borderColor: "#E5E7EB" },
                                                    "&.Mui-focused fieldset": { borderColor: THEME_COLOR }
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        {/* Assigned To Filter */}
                        {queryParam.tab !== "assigned" && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    multiple
                                    options={users}
                                    getOptionLabel={(option) => option.name}
                                    value={users.filter(u => (queryParam.assignedToIds || "").split(",").includes(u._id)) || []}
                                    onChange={(e, v) => {
                                        setQueryParam({
                                            ...queryParam,
                                            assignedToIds: v.length ? v.map(u => u._id).join(",") : null,
                                        });
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Filter by Assignee"
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "8px",
                                                    bgcolor: "#FFFFFF",
                                                    "& fieldset": { borderColor: "#E5E7EB" },
                                                    "&.Mui-focused fieldset": { borderColor: THEME_COLOR }
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        {/* Date Picker */}
                        <Grid item xs={12}>
                            <RangePicker
                                value={dateRange}
                                onChange={(date) => {
                                    if (date && Array.isArray(date)) {
                                        setDateRange(date);
                                        setQueryParam({
                                            ...queryParam,
                                            date,
                                            startDate: date[0] ? dayjs(date[0]).format("YYYY-MM-DD") : null,
                                            endDate: date[1] ? dayjs(date[1]).format("YYYY-MM-DD") : null,
                                        });
                                    } else {
                                        setDateRange(null);
                                        setQueryParam({
                                            ...queryParam,
                                            date: null,
                                            startDate: null,
                                            endDate: null,
                                        });
                                    }
                                }}
                                style={{
                                    width: "100%",
                                    height: "48px",
                                    borderRadius: "8px",
                                    border: "1px solid #E5E7EB"
                                }}
                                getPopupContainer={(trigger) => document.body}
                                popupStyle={{ zIndex: 99999 }}
                            />
                        </Grid>

                        {/* Priority Filter */}
                        {queryParam.tab !== "all" && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    options={priorityOptions}
                                    disableClearable={true}
                                    value={priorityOptions.find((o) => o.value === queryParam.priority) || priorityOptions[0]}
                                    onChange={(e, v) => {
                                        setQueryParam({
                                            ...queryParam,
                                            priority: v ? v.value : null,
                                        });
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Filter by Priority"
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "8px",
                                                    bgcolor: "#FFFFFF",
                                                    "& fieldset": { borderColor: "#E5E7EB" },
                                                    "&.Mui-focused fieldset": { borderColor: THEME_COLOR }
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        {/* Status Filter */}
                        {queryParam.tab !== "all" && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    options={statusOptions}
                                    disableClearable={true}
                                    value={statusOptions.find((o) => o.value === queryParam.status) || statusOptions[0]}
                                    onChange={(e, v) => {
                                        setQueryParam({
                                            ...queryParam,
                                            status: v ? v.value : null,
                                        });
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Filter by Status"
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "8px",
                                                    bgcolor: "#FFFFFF",
                                                    "& fieldset": { borderColor: "#E5E7EB" },
                                                    "&.Mui-focused fieldset": { borderColor: THEME_COLOR }
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        <Grid item xs={12} mt={2}>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={() => setShowFilters(false)}
                                sx={{
                                    bgcolor: THEME_COLOR,
                                    borderRadius: "8px",
                                    height: "48px",
                                    textTransform: "none",
                                    fontSize: "16px",
                                    "&:hover": { bgcolor: "#006666" }
                                }}
                            >
                                Show Results
                            </Button>
                        </Grid>
                    </Grid>
                </Drawer>

                <Box sx={{ mt: 2 }}>
                    {queryParam.tab === "all" ? (
                        <TodoTaskBoard
                            data={data}
                            handleView={handleView}
                            handleEdit={handleEdit}
                            handleDelete={handleDelete}
                            handleHistory={handleHistory}
                            refreshData={getTodoTaskData}
                            userId={user._id || user.id || user.userId}
                        />
                    ) : (
                        <MasterCommonTable
                            tableConfig={tableConfig}
                            data={data}
                            columns={columns}
                            handleEdit={handleEdit}
                            handleView={handleView}
                            handleDelete={handleDelete}
                            handleHistory={handleHistory}
                            tableTotalCount={tableTotalCount}
                            tableOutputEvent={tableOutputEvent}
                            isServerSide={true}
                        />
                    )}
                </Box>

                <Dialog
                    open={openDrawer}
                    PaperProps={{
                        style: {
                            width: "100%",
                            height: "fit-content",
                        },
                    }}
                    fullWidth
                    maxWidth="sm"
                >
                    {openDrawer ? (
                        <AddTodoTask
                            selectedData={selectedData}
                            setOpenDrawer={setOpenDrawer}
                            formType={selectedData.isView ? "view" : selectedData._id ? "edit" : "add"}
                            GetData={getTodoTaskData}
                        />
                    ) : null}
                </Dialog>

                <Dialog
                    open={openHistoryDrawer}
                    PaperProps={{
                        style: {
                            width: "100%",
                            height: "fit-content",
                        },
                    }}
                    fullWidth
                    maxWidth="sm"
                >
                    {openHistoryDrawer ? (
                        <TodoTaskHistory
                            selectedData={selectedData}
                            setOpenHistoryDrawer={setOpenHistoryDrawer}
                            users={users}
                        />
                    ) : null}
                </Dialog>
            </Box>
        </div >
    );
};

export default TodoTask;
